import { demoAudit, demoProjects, demoSubmissions, demoUsers } from "./demo";
import type { Project, ProjectVisit, Submission, User } from "./types";

const API_BASE = import.meta.env.VITE_API_BASE || "/api";
export const isDemo = import.meta.env.VITE_DEMO_MODE !== "false" && !import.meta.env.VITE_API_BASE;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.message || "请求失败，请稍后重试");
  return body as T;
}

function delay(ms = 260) { return new Promise((resolve) => setTimeout(resolve, ms)); }

function projectWithAdminNotes(project: Project, viewer: User): Project {
  const showNotes = viewer.role === "super_admin";
  const owner = demoUsers.find((item) => item.id === project.ownerId);
  return {
    ...project,
    ownerName: owner?.displayName || project.ownerName,
    ownerRole: owner?.role,
    ownerAdminNote: showNotes ? owner?.adminNote || "" : undefined,
    assignedAdmins: (project.assignedAdmins || []).map((admin) => {
      const liveUser = demoUsers.find((item) => item.id === admin.id);
      return {
        id: admin.id,
        username: liveUser?.username || admin.username,
        displayName: liveUser?.displayName || admin.displayName,
        ...(showNotes && liveUser?.adminNote ? { adminNote: liveUser.adminNote } : {}),
      };
    }),
  };
}

export const api = {
  async login(username: string, password: string, rememberMe = false): Promise<User> {
    if (!isDemo) return request<User>("/admin/auth/login", { method: "POST", body: JSON.stringify({ username, password, rememberMe }) });
    await delay();
    const user = demoUsers.find((item) => item.username === username && item.active);
    if (!user || password !== "Admin123!") throw new Error("账号或密码不正确");
    (rememberMe ? localStorage : sessionStorage).setItem("demo-user", user.id);
    return user;
  },
  async me(): Promise<User | null> {
    if (!isDemo) return request<User>("/admin/auth/me").catch(() => null);
    const id = sessionStorage.getItem("demo-user") || localStorage.getItem("demo-user");
    return demoUsers.find((item) => item.id === id) || null;
  },
  async logout() {
    if (!isDemo) await request("/admin/auth/logout", { method: "POST" });
    sessionStorage.removeItem("demo-user");
    localStorage.removeItem("demo-user");
  },
  async changePassword(currentPassword: string, newPassword: string): Promise<User> {
    if (!isDemo) return request<User>("/admin/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) });
    const user = await this.me(); if (!user) throw new Error("请先登录"); user.mustChangePassword = false; return user;
  },
  async register(input: { username: string; displayName: string; password: string }): Promise<User> {
    if (!isDemo) return request<User>("/admin/auth/register", { method: "POST", body: JSON.stringify(input) });
    if (demoUsers.some((item) => item.username.toLowerCase() === input.username.toLowerCase())) throw new Error("账号已存在");
    const user: User = { id: crypto.randomUUID(), username: input.username, displayName: input.displayName, role: "sub_admin", active: false, pendingApproval: true, createdAt: new Date().toISOString() };
    demoUsers.push(user); return user;
  },
  async publicProject(token: string): Promise<Project> {
    if (!isDemo) return request<Project>(`/public/projects/${encodeURIComponent(token)}`);
    await delay(120);
    return demoProjects.find((project) => project.publicToken === token) || demoProjects[0];
  },
  async projects(user: User): Promise<Project[]> {
    if (!isDemo) return request<Project[]>("/admin/projects");
    await delay(120);
    const visible = user.role === "super_admin" || user.role === "general_admin" ? demoProjects : demoProjects.filter((p) => p.ownerId === user.id || p.assignedAdmins.some((admin) => admin.id === user.id));
    return visible.map((project) => projectWithAdminNotes(project, user));
  },
  async submissions(projectId: string): Promise<Submission[]> {
    if (!isDemo) return request<Submission[]>(`/admin/projects/${projectId}/submissions`);
    await delay(120); return demoSubmissions.filter((item) => item.projectId === projectId);
  },
  async users(): Promise<User[]> {
    if (!isDemo) return request<User[]>("/admin/users");
    return [...demoUsers];
  },
  async projectAssignees(projectId: string): Promise<User[]> {
    if (!isDemo) return request<User[]>(`/admin/projects/${projectId}/assignees`);
    const viewer = await this.me(); const project = demoProjects.find((item) => item.id === projectId); if (!viewer || !project) throw new Error("项目不存在");
    const owner = demoUsers.find((item) => item.id === project.ownerId); if (viewer.role === "general_admin" && owner?.role === "super_admin") throw new Error("总管理员不能为超级管理员创建的项目分配协作管理员");
    const roles = viewer.role === "super_admin" ? ["sub_admin", "general_admin"] : ["sub_admin"];
    return demoUsers.filter((item) => roles.includes(item.role) && item.active && !item.pendingApproval);
  },
  async createUser(input: { username: string; displayName: string; password: string; adminNote?: string }): Promise<User> {
    if (!isDemo) return request<User>("/admin/users", { method: "POST", body: JSON.stringify(input) });
    const user: User = { id: crypto.randomUUID(), username: input.username, displayName: input.displayName, role: "sub_admin", active: true, pendingApproval: false, adminNote: input.adminNote || "", mustChangePassword: true, createdAt: new Date().toISOString() };
    demoUsers.push(user); return user;
  },
  async updateUser(userId: string, input: { active?: boolean; pendingApproval?: boolean; approve?: boolean; password?: string; adminNote?: string; role?: "sub_admin" | "general_admin" }): Promise<User> {
    if (!isDemo) return request<User>(`/admin/users/${userId}`, { method: "PATCH", body: JSON.stringify(input) });
    const user = demoUsers.find((item) => item.id === userId); if (!user) throw new Error("账号不存在");
    if (typeof input.active === "boolean") user.active = input.active; if (typeof input.pendingApproval === "boolean") user.pendingApproval = input.pendingApproval; if (typeof input.adminNote === "string") user.adminNote = input.adminNote; if (input.role) user.role = input.role; if (input.approve) { user.active = true; user.pendingApproval = false; } if (input.password) user.mustChangePassword = true; return { ...user };
  },
  async assignProject(projectId: string, userIds: string[]): Promise<Project> {
    if (!isDemo) return request<Project>(`/admin/projects/${projectId}/assign`, { method: "POST", body: JSON.stringify({ userIds }) });
    const project = demoProjects.find((item) => item.id === projectId); if (!project) throw new Error("项目不存在");
    project.assignedAdmins = demoUsers.filter((item) => userIds.includes(item.id) && item.role !== "super_admin" && item.active).map(({ id, username, displayName }) => ({ id, username, displayName }));
    const viewer = await this.me();
    return projectWithAdminNotes(project, viewer || demoUsers[0]);
  },
  async shortenProjectLink(projectId: string): Promise<Project> {
    if (!isDemo) return request<Project>(`/admin/projects/${projectId}/short-link`, { method: "POST" });
    const project = demoProjects.find((item) => item.id === projectId); if (!project) throw new Error("项目不存在");
    if (project.publicToken.length > 10) project.publicToken = Math.random().toString(36).slice(2, 10);
    return { ...project };
  },
  async setProjectArchived(projectId: string, archived: boolean): Promise<Project> {
    if (!isDemo) return request<Project>(`/admin/projects/${projectId}`, { method: "PATCH", body: JSON.stringify({ archived }) });
    const project = demoProjects.find((item) => item.id === projectId); if (!project) throw new Error("项目不存在");
    if (archived) { project.archivedAt = new Date().toISOString(); project.archivedBy = "本地演示管理员"; } else { delete project.archivedAt; delete project.archivedBy; }
    return { ...project };
  },
  async updateProjectSettings(projectId: string, input: { name?: string; deadline?: string; maxPersonBytes?: number }): Promise<Project> {
    if (!isDemo) return request<Project>(`/admin/projects/${projectId}`, { method: "PATCH", body: JSON.stringify(input) });
    const project = demoProjects.find((item) => item.id === projectId); if (!project) throw new Error("项目不存在"); Object.assign(project, input); if (input.deadline) { delete project.archivedAt; delete project.archivedBy; } return { ...project };
  },
  async settings(): Promise<{ maxPersonBytes: number; updatedAt?: string; updatedBy?: string }> {
    if (!isDemo) return request("/admin/settings"); return { maxPersonBytes: 15 * 1024 * 1024 };
  },
  async updateSettings(maxPersonBytes: number): Promise<{ maxPersonBytes: number; updatedAt?: string; updatedBy?: string }> {
    if (!isDemo) return request("/admin/settings", { method: "PATCH", body: JSON.stringify({ maxPersonBytes }) }); return { maxPersonBytes, updatedAt: new Date().toISOString(), updatedBy: "超级管理员" };
  },
  async downloadFile(projectId: string, fileId: string): Promise<{ url: string; size: number }> {
    if (!isDemo) return request(`/admin/projects/${projectId}/files/${fileId}/download`, { method: "POST" });
    const file = demoSubmissions.flatMap((item) => item.files).find((item) => item.id === fileId); if (!file?.previewUrl) throw new Error("音频不存在"); return { url: file.previewUrl, size: file.size };
  },
  async deleteAudio(projectId: string, fileId: string): Promise<{ deletedFileId: string; removedSubmission: boolean; restoredBytes: number; project: Project }> {
    if (!isDemo) return request(`/admin/projects/${projectId}/files/${fileId}`, { method: "DELETE" });
    const project = demoProjects.find((item) => item.id === projectId); if (!project) throw new Error("项目不存在");
    const submission = demoSubmissions.find((item) => item.files.some((file) => file.id === fileId)); const file = submission?.files.find((item) => item.id === fileId); if (!submission || !file) throw new Error("音频不存在或已删除");
    submission.files = submission.files.filter((item) => item.id !== fileId); if (!submission.files.length) demoSubmissions.splice(demoSubmissions.indexOf(submission), 1);
    project.fileCount = Math.max(0, project.fileCount - 1); project.bytes = Math.max(0, project.bytes - file.size); project.roles = [...new Set(demoSubmissions.flatMap((item) => item.files.map((item) => item.roleName)))];
    return { deletedFileId: fileId, removedSubmission: !submission.files.length, restoredBytes: file.size, project: { ...project } };
  },
  async projectVisits(projectId: string): Promise<{ total: number; visits: ProjectVisit[] }> {
    if (!isDemo) return request(`/admin/projects/${projectId}/visits`);
    return { total: 0, visits: [] };
  },
  async audits(actorId?: string) {
    if (!isDemo) return request<typeof demoAudit>(`/admin/audit${actorId ? `?actorId=${encodeURIComponent(actorId)}` : ""}`);
    return actorId ? demoAudit.filter((entry) => entry.actorId === actorId) : [...demoAudit];
  },
  async createProject(input: Pick<Project, "name" | "description" | "deadline">, user: User): Promise<Project> {
    if (!isDemo) return request<Project>("/admin/projects", { method: "POST", body: JSON.stringify(input) });
    const project: Project = { id: crypto.randomUUID(), ...input, ownerId: user.id, ownerName: user.displayName, assignedAdmins: [], publicToken: crypto.randomUUID().replaceAll("-", ""), status: "accepting", createdAt: new Date().toISOString(), submissions: 0, fileCount: 0, bytes: 0, roles: [] };
    demoProjects.unshift(project); return project;
  },
  async renameRole(projectId: string, from: string, to: string) {
    if (!isDemo) return request<{ project: Project; changed: number }>(`/admin/projects/${projectId}/roles/rename`, { method: "POST", body: JSON.stringify({ from, to }) });
    return { project: demoProjects.find((p) => p.id === projectId)!, changed: demoSubmissions.flatMap((s) => s.files).filter((f) => f.roleName === from).length };
  },
  async recordSync(projectId: string, result: { downloaded: number; downloadedBytes?: number; skipped: number; failed: number }) {
    if (!isDemo) return request(`/admin/projects/${projectId}/sync-runs`, { method: "POST", body: JSON.stringify(result) });
    return result;
  },
  async ossUsage(): Promise<{ storageBytes: number; monthDownloadBytes: number; storageUnitPrice: number; trafficUnitPrice: number; estimatedStorageFee: number; estimatedTrafficFee: number; estimatedTotal: number; currency: string; trackingSince: string }> {
    if (!isDemo) return request("/admin/oss-usage");
    const storageBytes = demoProjects.reduce((sum, project) => sum + project.bytes, 0); return { storageBytes, monthDownloadBytes: 0, storageUnitPrice: .12, trafficUnitPrice: .5, estimatedStorageFee: storageBytes / 1073741824 * .12, estimatedTrafficFee: 0, estimatedTotal: storageBytes / 1073741824 * .12, currency: "CNY", trackingSince: "本功能启用后" };
  },
  async cleanupProject(projectId: string): Promise<Project> {
    if (!isDemo) return request<Project>(`/admin/projects/${projectId}/cleanup`, { method: "POST" });
    const project = demoProjects.find((p) => p.id === projectId); if (!project) throw new Error("项目不存在"); project.status = "cloud_deleted"; project.bytes = 0; return { ...project };
  },
  async submitDemo() { await delay(520); return { receiptId: `CMV-${new Date().toISOString().slice(0, 10).replaceAll("-", "")}-${String(Math.floor(Math.random() * 9999)).padStart(4, "0")}` }; },
};
